import { useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import {
  addCredit,
  createBudget,
  deleteBudget,
  estimateCost,
  exportUsage,
  generateInvoice,
  getAccount,
  getRates,
  getUsage,
  listBudgets,
  listInvoices,
  payInvoice,
  reactivateAccount,
  setUsageExportDelivery,
  suspendAccount,
  triggerSample,
} from "./api";
import type {
  AccountSummary,
  BillingRateTable,
  Budget,
  BudgetCreate,
  CostEstimateResponse,
  Invoice,
  PlannedResource,
  SampleUsageResponse,
  UsageExportQuery,
  UsageQuery,
  UsageSummary,
} from "./types";

export const billingKeys = {
  all: ["billing"] as const,
  usage: (params: UsageQuery) => ["billing", "usage", params] as const,
  rates: () => ["billing", "rates"] as const,
  account: () => ["billing", "account"] as const,
  invoices: (projectId?: string) =>
    ["billing", "invoices", projectId ?? null] as const,
  estimate: (key: string) => ["billing", "estimate", key] as const,
  budgets: () => ["billing", "budgets"] as const,
};

/** Debounce a serialisable value so slider drags/typing don't spam the API. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Server-side cost estimate for a list of planned resources (POST
 * /billing/estimate — same rate book the billing engine uses). Items are
 * debounced and keyed by content, so the same spec is cached and an in-flight
 * slider drag only fires once settled. Errors are silent by design: the
 * consumer renders nothing.
 *
 * `updating` is true while `data` is not the figure for `resources` as they
 * are now — during the debounce and, with `keepPrevious` (the previous
 * figure stays up instead of the estimate vanishing), during the request.
 * Platform test CMP-14: the old price stood beside the new size with nothing
 * to say it was old.
 */
export function useCostEstimate(
  resources: PlannedResource[],
  options: { keepPrevious?: boolean } = {},
) {
  // Key by content (not array identity) so inline literals don't refetch.
  const current = JSON.stringify(resources);
  const serialized = useDebouncedValue(current, 400);
  const query = useQuery<CostEstimateResponse>({
    queryKey: billingKeys.estimate(serialized),
    queryFn: () => estimateCost(JSON.parse(serialized) as PlannedResource[]),
    enabled: serialized !== "[]",
    staleTime: 60_000,
    retry: false,
    ...(options.keepPrevious
      ? { placeholderData: (previous: CostEstimateResponse | undefined) => previous }
      : {}),
  });
  return { ...query, updating: current !== serialized || query.isPlaceholderData };
}

export function useAccount() {
  return useQuery<AccountSummary>({
    queryKey: billingKeys.account(),
    queryFn: getAccount,
  });
}

export function useInvoices(projectId?: string) {
  return useQuery<Invoice[]>({
    queryKey: billingKeys.invoices(projectId),
    queryFn: () => listInvoices(projectId),
  });
}

export function useGenerateInvoice(): UseMutationResult<
  Invoice,
  Error,
  { period_start: string; period_end: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateInvoice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function usePayInvoice(): UseMutationResult<Invoice, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: payInvoice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useUsage(params: UsageQuery, options: { enabled?: boolean } = {}) {
  return useQuery<UsageSummary>({
    queryKey: billingKeys.usage(params),
    queryFn: () => getUsage(params),
    placeholderData: (previous) => previous,
    // SASE-30b: the layout's spend figure does not ask in the SASE edition
    enabled: options.enabled ?? true,
  });
}

/**
 * This month's spend so far and the month-end forecast: the one read behind
 * the dashboard tile (and, once the header uses it, the header). The figures
 * are the API's (`month_to_date_cost`, `projected_cost`), which do not depend
 * on the window asked for, so the window here is only "the last hour" and
 * the key is the same for every caller: one request, one number. Refreshed
 * every five minutes — the header's own read was frozen at the moment the
 * console was opened.
 */
export function useMonthSpend(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...billingKeys.all, "month-spend"] as const,
    queryFn: () => {
      const end = new Date();
      const start = new Date(end.getTime() - 3_600_000);
      return getUsage({ start: start.toISOString(), end: end.toISOString() });
    },
    select: (usage: UsageSummary) => ({
      // An API from before the field: the window's total is all there is.
      spend: usage.month_to_date_cost ?? usage.total_cost,
      projected: usage.projected_cost,
    }),
    enabled: options.enabled ?? true,
    refetchInterval: 5 * 60_000,
  });
}

export function useRates() {
  return useQuery<BillingRateTable>({
    queryKey: billingKeys.rates(),
    queryFn: getRates,
  });
}

export function useTriggerSample(): UseMutationResult<
  SampleUsageResponse,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerSample,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useAddCredit(): UseMutationResult<
  void,
  Error,
  { userId: string; amount: number }
> {
  return useMutation({
    mutationFn: ({ userId, amount }) => addCredit(userId, amount),
  });
}

export function useSuspendAccount(): UseMutationResult<void, Error, string> {
  return useMutation({ mutationFn: suspendAccount });
}

export function useReactivateAccount(): UseMutationResult<void, Error, string> {
  return useMutation({ mutationFn: reactivateAccount });
}

// --- usage export, budgets (metering plan Item 7) ------------------------ //
export function useExportUsage(): UseMutationResult<
  { blob: Blob; filename: string },
  Error,
  UsageExportQuery
> {
  return useMutation({ mutationFn: exportUsage });
}

export function useSetUsageExportDelivery(): UseMutationResult<
  AccountSummary,
  Error,
  boolean
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setUsageExportDelivery,
    onSuccess: (account) => {
      queryClient.setQueryData(billingKeys.account(), account);
    },
  });
}

export function useBudgets(enabled = true) {
  return useQuery<Budget[]>({
    queryKey: billingKeys.budgets(),
    queryFn: listBudgets,
    enabled,
  });
}

export function useCreateBudget(): UseMutationResult<Budget, Error, BudgetCreate> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBudget,
    onSuccess: () => {
      // The whole billing family, not the budget list alone: the new
      // budget's reading is taken now, and the page's "Projected month-end"
      // beside it must be of the same moment (round 4: $57.78 on the budget
      // beside a projection read minutes earlier).
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useDeleteBudget(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBudget,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.budgets() });
    },
  });
}
